import { useState, useEffect } from "react";
import { API } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Shield,
  Users,
  TrendingUp,
  AlertTriangle,
  Clock,
  RefreshCw,
  Activity,
  Target,
  ArrowUpRight,
  BarChart3,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, color = "text-slate-600" }) {
  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200">
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <span>{label}</span>
      </div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function RepRow({ rep }) {
  const overdueColor = rep.overdue_pct > 30 ? "text-red-400" : rep.overdue_pct > 15 ? "text-amber-400" : "text-green-400";
  const winColor = rep.win_ratio_pct > 20 ? "text-green-400" : rep.win_ratio_pct > 10 ? "text-amber-400" : "text-slate-600";

  return (
    <div data-testid={`rep-row-${rep.user_id}`} className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-slate-900">{rep.name}</h3>
          <p className="text-xs text-slate-500">{rep.sales_role}</p>
        </div>
        <Badge variant="outline" className="bg-slate-700/50 text-slate-300 border-slate-600">
          {rep.total_cards} cards
        </Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <div className="text-xs text-slate-500">Active</div>
          <div className="text-lg font-semibold text-slate-900">{rep.active_cards}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Overdue</div>
          <div className={`text-lg font-semibold ${overdueColor}`}>{rep.overdue_pct}%</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Won</div>
          <div className="text-lg font-semibold text-green-400">{rep.won_count}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Win Ratio</div>
          <div className={`text-lg font-semibold ${winColor}`}>{rep.win_ratio_pct}%</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Reactivation</div>
          <div className="text-lg font-semibold text-purple-400">{rep.reactivation_conversion_pct}%</div>
        </div>
      </div>
    </div>
  );
}

export default function MPDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/mp-dashboard?days=${days}`, { credentials: "include" });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch MP dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [days]);

  if (loading) return <div className="text-center py-16 text-slate-500">Loading dashboard...</div>;
  if (!data) return <div className="text-center py-16 text-slate-500">Access denied or no data</div>;

  const totalActive = data.per_user?.reduce((s, r) => s + r.active_cards, 0) || 0;
  const totalWon = data.per_user?.reduce((s, r) => s + r.won_count, 0) || 0;
  const avgOverdue = data.per_user?.length
    ? Math.round(data.per_user.reduce((s, r) => s + r.overdue_pct, 0) / data.per_user.length)
    : 0;

  return (
    <div data-testid="mp-dashboard-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-400" />
            Managing Partner Dashboard
          </h1>
          <p className="text-sm text-slate-600 mt-1">Private commercial performance view ({data.period_days}-day window)</p>
        </div>
        <Button
          data-testid="refresh-mp-btn"
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="border-slate-200 text-slate-300"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Summary Metrics */}
      <div data-testid="mp-metrics" className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Activity} label="Active Cards" value={totalActive} color="text-cyan-400" />
        <StatCard icon={TrendingUp} label="Won Deals" value={totalWon} sub={`Last ${days} days`} color="text-green-400" />
        <StatCard icon={Clock} label="Avg Overdue" value={`${avgOverdue}%`} color={avgOverdue > 20 ? "text-red-400" : "text-amber-400"} />
        <StatCard
          icon={AlertTriangle}
          label="Escalations"
          value={data.escalation_count}
          color={data.escalation_count > 0 ? "text-red-400" : "text-green-400"}
        />
      </div>

      {/* Per-User Breakdown */}
      <div>
        <h2 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" /> Team Performance
        </h2>
        {data.per_user?.length > 0 ? (
          <div data-testid="team-performance" className="grid gap-3">
            {data.per_user.map((rep) => (
              <RepRow key={rep.user_id} rep={rep} />
            ))}
          </div>
        ) : (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-8 text-center text-slate-500 text-sm">
              No sales reps configured. Assign sales roles in User Management.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Escalations */}
      {data.escalations?.length > 0 && (
        <div>
          <h2 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Active Escalations
          </h2>
          <div data-testid="escalations-list" className="grid gap-2">
            {data.escalations.map((e, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3"
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${e.severity === "high" ? "bg-red-500" : "bg-amber-500"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-900 truncate">{e.title}</p>
                  <p className="text-[10px] text-slate-500">{e.type?.replace(/_/g, " ")} | {e.column || ""}</p>
                </div>
                <Badge variant="outline" className={e.severity === "high" ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-amber-500/10 text-amber-400 border-amber-500/20"}>
                  {e.severity}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
